import { COMBO_NAMES } from '../content/combos';
import type { Ability, LearnSource, Piece, PieceKind } from './types';

export const BASE_ABILITIES: Record<PieceKind, readonly Ability[]> = {
  pawn: ['pawn'],
  knight: ['knight'],
  bishop: ['bishop'],
  rook: ['rook'],
  queen: ['bishop', 'rook'],
  king: ['king'],
  blocker: [],
  dormant: [],
};

export const KIND_NAMES: Record<PieceKind, string> = {
  pawn: 'pawn',
  knight: 'knight',
  bishop: 'bishop',
  rook: 'rook',
  queen: 'queen',
  king: 'king',
  blocker: 'blocker',
  dormant: 'dormant',
};

export const ABILITY_NAMES: Record<Ability, string> = {
  pawn: 'pawn steps',
  knight: 'knight jumps',
  bishop: 'diagonals',
  rook: 'straight lines',
  king: 'king steps',
  hop: 'two square hops',
  cannon: 'cannon hops',
};

/** Abilities that apply right now, including temporary ones. A dormant piece has none. */
export const effectiveAbilities = (piece: Piece): readonly Ability[] => {
  if (piece.dormantTurns > 0) return [];
  if (piece.knightTurns > 0 && !piece.abilities.includes('knight')) {
    return [...piece.abilities, 'knight'];
  }
  return piece.abilities;
};

export const hasAbility = (piece: Piece, ability: Ability): boolean =>
  effectiveAbilities(piece).includes(ability);

export const addAbilities = (current: readonly Ability[], added: readonly Ability[]): Ability[] => {
  const result = [...current];
  for (const ability of added) {
    if (!result.includes(ability)) result.push(ability);
  }
  return result;
};

/** The sources of the abilities, with the one source for each ability the piece did not have. */
export const addLearnSources = (
  current: Piece['learnedFrom'],
  added: readonly Ability[],
  source: LearnSource,
): Piece['learnedFrom'] => {
  const next = { ...current };
  for (const ability of added) next[ability] = source;
  return next;
};

const hasAll = (abilities: readonly Ability[], wanted: readonly Ability[]): boolean =>
  wanted.every((ability) => abilities.includes(ability));

const sameSet = (a: readonly Ability[], b: readonly Ability[]): boolean =>
  a.length === b.length && hasAll(a, b);

const NAMED_KINDS: readonly PieceKind[] = ['queen', 'rook', 'bishop', 'knight', 'pawn', 'king'];

/**
 * The silhouette that fits an ability set. An exact match wins. A set that
 * still holds the current kind's base keeps the kind. Otherwise the piece is
 * drawn as the biggest named kind it contains, or a pawn.
 */
export const kindForAbilities = (abilities: readonly Ability[], current: PieceKind): PieceKind => {
  const exact = NAMED_KINDS.find((kind) => sameSet(abilities, BASE_ABILITIES[kind]));
  if (exact) return exact;
  if (hasAll(abilities, BASE_ABILITIES[current])) return current;
  const contained = NAMED_KINDS.find(
    (kind) => BASE_ABILITIES[kind].length > 0 && hasAll(abilities, BASE_ABILITIES[kind]),
  );
  return contained ?? 'pawn';
};

/**
 * A name for what the piece has become. A named combination comes from
 * src/content/combos.ts. Anything else is said as its parts: "pawn with
 * knight moves". The log and the inspector use it.
 */
export const comboName = (piece: Piece): string => {
  const abilities = effectiveAbilities(piece);
  const extras = abilities.filter((ability) => !BASE_ABILITIES[piece.kind].includes(ability));
  if (piece.kind === 'dormant') return KIND_NAMES.dormant;
  const named = COMBO_NAMES.find((combo) => hasAll(abilities, combo.abilities));
  if (named) return named.name;
  if (extras.length === 0) return KIND_NAMES[piece.kind];
  return `${KIND_NAMES[piece.kind]} with ${extras.map((ability) => ABILITY_NAMES[ability]).join(' and ')}`;
};

/** True when the combination has a name of its own, not just its parts. */
export const hasComboName = (piece: Piece): boolean =>
  COMBO_NAMES.some((combo) => hasAll(effectiveAbilities(piece), combo.abilities));
